import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui';
import { CloseIcon } from '@/components/icons';
import Input from '@/components/ui/Input/Input';

const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Old password is required'),
    newPassword: z
      .string()
      .min(8, 'New password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).+$/,
        'New password must include uppercase, lowercase, number, and special character'
      ),
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.newPassword !== data.oldPassword, {
    message: 'New password must be different from old password',
    path: ['newPassword'],
  })
  .refine((data) => data.confirmPassword === data.newPassword, {
    message: 'Confirm password does not match new password',
    path: ['confirmPassword'],
  });

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (data: { oldPassword: string; newPassword: string }) => Promise<void> | void;
  isUpdating?: boolean;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  isUpdating = false,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ oldPassword: '', newPassword: '', confirmPassword: '' });
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: ChangePasswordFormData) => {
    await onUpdate({
      oldPassword: data.oldPassword,
      newPassword: data.newPassword,
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Change password"
    >
      <div
        className="m-4 w-full max-w-lg rounded-xl bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 flex flex-col gap-4">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Change Password</h2>
              <p className="mt-1 text-sm text-gray-500">Update your account password securely.</p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close modal" disabled={isUpdating}>
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="gap-6 flex flex-col">
            <Controller
              name="oldPassword"
              control={control}
              render={({ field }) => (
                <Input
                  label="Old Password"
                  type="password"
                  placeholder="Enter old password"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.oldPassword?.message}
                  disabled={isUpdating}
                  $marginbottom="0"
                />
              )}
            />

            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <Input
                  label="New Password"
                  type="password"
                  placeholder="Enter new password"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.newPassword?.message}
                  disabled={isUpdating}
                  $marginbottom="0"
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <Input
                  label="Confirm Password"
                  type="password"
                  placeholder="Confirm new password"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.confirmPassword?.message}
                  disabled={isUpdating}
                  $marginbottom="0"
                />
              )}
            />

            <Button type="submit" variant="primary" className="w-full mt-4 inline-flex" disabled={!isValid || isUpdating}>
              {isUpdating ? 'Changing Password...' : 'Change Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
